import { Button } from "@/components"
import { useEffect, useState } from "react"
import { Plus } from "lucide-react"
import { useTranslation } from "react-i18next"
import { GLOB_PATTERN_URL, validateGlobPattern } from "@/pages/settings/reviewer/helpers"
import { RepoPathsSelector } from "@/pages/settings/reviewer/repoPathsSelector"
import { RepoPaths } from "@/models/ReviewerConfig"

export interface RepositoryOption {
  name: string
  value: string
}

interface ExcludeConfigManagerProps {
  repositories: RepositoryOption[]
  repoPaths: RepoPaths[]
  onConfigChange: (config: RepoPaths | null) => void
  onConfigDelete: (repoName: string) => void
  disabled: boolean
}

export interface RepoPathConfig {
  id: string
  open: boolean
  searchValue: string
  selectedRepo: string
  paths: string[]
  error: string
  isExisting: boolean
}

export const ExcludeConfigManager = ({
  repositories,
  repoPaths = [],
  onConfigDelete,
  onConfigChange,
  disabled
}: ExcludeConfigManagerProps) => {
  const { t } = useTranslation("Integrations")
  const [repoPathsConfigs, setRepoPathsConfigs] = useState<RepoPathConfig[]>([])
  const isAddButtonDisabled = repoPathsConfigs[repoPathsConfigs.length - 1]?.id === "new"
  const tagsElements = document.querySelectorAll("span.rti--tag")

  const handleAddNew = () => {
    setRepoPathsConfigs((prev) => [
      ...prev.filter((config) => config.id !== "new"),
      {
        id: "new",
        open: false,
        searchValue: "",
        selectedRepo: "",
        paths: [],
        error: "",
        isExisting: false
      }
    ])
  }

  useEffect(() => {
    setRepoPathsConfigs(
      repoPaths.map((repo) => ({
        id: repo.repoId,
        open: false,
        searchValue: "",
        selectedRepo: repo.repoName,
        paths: repo.paths,
        error: "",
        isExisting: true
      }))
    )
  }, [repoPaths])

  useEffect(() => {
    tagsElements?.forEach((tag) => {
      if (tag.textContent && validateGlobPattern(tag.textContent)) {
        tag.classList.remove("tag-error-state")
      } else {
        tag.classList.add("tag-error-state")
      }
    })
  }, [tagsElements])

  return (
    <div className="mb-10 mt-2 space-y-3 text-primary">
      <h3 className="text-sm font-medium">{t("ExcludedPaths")}</h3>
      <p className="justify-center text-xs text-secondary">
        {t("SpecifyPaths")}
        <a className="ml-1 text-link" href={GLOB_PATTERN_URL} target="_blank" rel="noopener noreferrer">
          {t("GlobPatterns")}
        </a>
      </p>

      <RepoPathsSelector
        repositories={repositories}
        onConfigChange={onConfigChange}
        onConfigDelete={onConfigDelete}
        disabled={disabled}
        repoPathsConfigs={repoPathsConfigs}
        setRepoPathsConfigs={(repoPathConfig: RepoPathConfig[]) => setRepoPathsConfigs(repoPathConfig)}
      />

      <Button
        disabled={disabled || isAddButtonDisabled}
        onClick={handleAddNew}
        className={`rounded-md p-2 text-xs text-primary ${disabled ? "disabled:pointer-events-auto disabled:cursor-not-allowed" : ""}`}
        variant="outline"
        size="sm"
      >
        <Plus className="mr-1 text-secondary" size={16} /> {t("AddExclude")}
      </Button>
    </div>
  )
}
